import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useState, useEffect } from "react";
import axios from "axios";
import BASEURL from "../Utils/Baseurl";
import moment from "moment";
import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  Grid2,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import useBranchAccess from "../../hooks/useBranchAccess";

const localizer = momentLocalizer(moment);

const HRCalendar = () => {
  const { isBranchAdmin, userBranch } = useBranchAccess();
  const [branchData, setBranchData] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const theme = useTheme();

  useEffect(() => {
    if (isBranchAdmin && userBranch) {
      setSelectedBranch({ value: userBranch, label: userBranch });
    }
  }, [isBranchAdmin, userBranch]);

  // Fetch branchwise attendance summary
  useEffect(() => {
    if (isBranchAdmin && !selectedBranch) return;
    const fetchBranchwiseData = async () => {
      setLoading(true);
      setError(null);
      const query = `
        query BranchwiseAttendance($date: String!) {
          branchwiseAttendanceSummary(date: $date) {
            branch
            totalEmployees
            checkedInToday
            notCheckedInToday
            employeeDetails {
              empid
              employeeName
              checkIn
              checkOut
              totalWorkingHours
              overtime
            }
          }
        }
      `;

      try {
        const response = await axios.post(BASEURL, {
          query: query,
          variables: { date: date },
        });
        const summary = response?.data?.data?.branchwiseAttendanceSummary || [];
        setBranchData(summary);

        // Build branch options
        const branches = summary.map((b) => ({
          value: b.branch,
          label: b.branch,
        }));
        setBranchOptions(branches);

        // Build employee options from all branches
        const allEmployees = [];
        summary.forEach((b) => {
          (b.employeeDetails || []).forEach((emp) => {
            allEmployees.push({
              value: emp.empid,
              label: `${emp.employeeName} (${emp.empid})`,
              branch: b.branch,
              ...emp,
            });
          });
        });
        setEmployeeOptions(allEmployees);

        setLoading(false);
      } catch (err) {
        setError("Error fetching attendance data.");
        setLoading(false);
        console.error("Error fetching branchwise attendance:", err);
      }
    };

    fetchBranchwiseData();
  }, [date, isBranchAdmin, selectedBranch]);

  // Filter employees based on selected branch
  const filteredEmployees = selectedBranch
    ? employeeOptions.filter((emp) => emp.branch === selectedBranch.value)
    : employeeOptions;

  // Build calendar events from filtered data
  useEffect(() => {
    let employeesToShow = [];

    if (selectedEmployee) {
      // Show only selected employee
      employeesToShow = employeeOptions.filter(
        (emp) => emp.value === selectedEmployee.value
      );
    } else if (selectedBranch) {
      // Show all employees in selected branch
      employeesToShow = employeeOptions.filter(
        (emp) => emp.branch === selectedBranch.value
      );
    } else {
      // Show all employees
      employeesToShow = employeeOptions;
    }

    const calendarEvents = employeesToShow
      .filter((emp) => emp.checkIn || emp.checkOut)
      .map((emp) => {
        const checkIn = emp.checkIn
          ? moment(emp.checkIn).format("hh:mm A")
          : "N/A";
        const checkOut = emp.checkOut
          ? moment(emp.checkOut).format("hh:mm A")
          : "In Progress";
        const totalWorkingHours = emp.totalWorkingHours || "N/A";
        const overTime = emp.overtime || "0:00";
        const isInProgress = emp.checkIn && !emp.checkOut;

        return {
          title: isInProgress
            ? `${emp.employeeName}\nIn: ${checkIn}\nStatus: In Progress`
            : `${emp.employeeName}\nIn: ${checkIn}\nOut: ${checkOut}\nTotal: ${totalWorkingHours} hrs\nOT: ${overTime} hrs`,
          start: emp.checkIn
            ? moment(emp.checkIn).toDate()
            : moment(emp.checkOut).toDate(),
          end: emp.checkOut
            ? moment(emp.checkOut).toDate()
            : moment(emp.checkIn).toDate(),
          totalWorkingHours: emp.totalWorkingHours,
          isInProgress,
          type: "attendance",
        };
      });

    setEvents(calendarEvents);
  }, [selectedBranch, selectedEmployee, employeeOptions]);

  const eventStyleGetter = (event) => {
    let backgroundColor = "#f38875";

    if (event.type === "attendance") {
      if (event.start && event.end && event.totalWorkingHours !== "N/A") {
        const hours = parseFloat(event.totalWorkingHours);
        if (hours >= 8) {
          backgroundColor = "#88fd8b";
        } else if (hours >= 0) {
          backgroundColor = "#22BCF2";
        }
      }
    }

    const style = {
      backgroundColor,
      borderRadius: "10px",
      opacity: 1,
      color: "white",
      border: "1px solid #fff",
      boxShadow: "0px 3px 5px rgba(0,0,0,0.2)",
      padding: "5px",
      display: "block",
    };

    return { style };
  };

  const CustomEvent = ({ event }) => {
    const lines = event.title.split("\n");

    return (
      <Box
        sx={{
          fontSize: "1rem",
          color: "#fff",
          padding: "2px",
          borderRadius: "5px",
          whiteSpace: "pre-wrap",
        }}
      >
        {lines.map((line, index) => (
          <p key={index} style={{ fontSize: "11px", margin: 0 }}>
            {line}
          </p>
        ))}
      </Box>
    );
  };

  const handleNavigate = (newDate) => {
    setDate(moment(newDate).format("YYYY-MM-DD"));
  };

  return (
    <DashboardLayout>
      <Box sx={{ padding: "20px", position: "relative" }}>
        <Typography variant="h5" fontWeight="bold" mb={2}>
          HR Attendance Calendar
        </Typography>

        <Grid2 container spacing={2} mb={2}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={branchOptions}
              value={selectedBranch}
              onChange={(event, newValue) => {
                setSelectedBranch(newValue);
                setSelectedEmployee(null);
              }}
              getOptionLabel={(option) => option.label}
              disabled={isBranchAdmin}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Filter by Branch"
                  placeholder="Select a branch"
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={filteredEmployees}
              value={selectedEmployee}
              onChange={(event, newValue) => {
                setSelectedEmployee(newValue);
              }}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Search Employee"
                  placeholder="Select an employee"
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              type="date"
              label="Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid2>
        </Grid2>

        {loading ? (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" variant="h6" align="center">
            {error}
          </Typography>
        ) : (
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: "16px",
              padding: "20px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              date={moment(date).toDate()}
              onNavigate={handleNavigate}
              style={{
                height: 700,
                width: "100%",
                borderRadius: "16px",
                marginBottom: "44px",
              }}
              eventPropGetter={eventStyleGetter}
              components={{
                event: CustomEvent,
              }}
            />
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default HRCalendar;
